import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGoBack } from '../../hooks/useUrlState';
import Swal from 'sweetalert2';
import api from '../../utils/api';
import RichTextEditor from '../../components/RichTextEditor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faSave, faPaperclip, faInfoCircle, faUsers, faSpinner,
    faClipboardList, faFilm, faFolderOpen, faBuilding, faCalendarDay, faRepeat,
    faFileCode
} from '@fortawesome/free-solid-svg-icons';
import AttachMenu from '../../components/AttachMenu';
import UploadProgress from '../../components/UploadProgress';
import EmployeeMultiSelect from '../../components/EmployeeMultiSelect';
import VendorSection from '../../components/VendorSection';
import RecentOptions from '../../components/RecentOptions';
import { recordRecentPick } from '../../utils/recentPicks';
import DatePickerField from '../../components/DatePickerField';
import TaskTimeWindow from '../../components/TaskTimeWindow';
import AttachmentRequirement from '../../components/AttachmentRequirement';
import PrivateTaskToggle from '../../components/PrivateTaskToggle';
import ScheduleProjection from '../../components/ScheduleProjection';
import { resolveMediaUrl } from '../../utils/taskHelpers';
import { datesBetween, prettyDate, todayYmd } from '../../utils/scheduleDates';
import '../../styles/App.css';
import '../../styles/tasks.css';
import '../../styles/recurring.css';

const MAX_VIDEO_MB = 300;

/**
 * Deliberately the same shape as AddTask: same two-column grid, same cards, the
 * same popover date picker and the same sticky footer. Editing a task and
 * creating one are the same job, and having them look different made this page
 * feel like a leftover.
 *
 * The one thing only this page can do is turn a one-off task into a recurring
 * schedule.
 */
const EditTask = () => {
    const navigate = useNavigate();
    const goBack = useGoBack('/tasks');
    const { id } = useParams();

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [isCompressing] = useState(false);

    const [projectsList, setProjectsList] = useState([]);
    const [employeesList, setEmployeesList] = useState([]);

    const [formData, setFormData] = useState({
        title: '', description: '', taskType: 'Project Task',
        projectId: '', priority: 'Medium', startDate: '', dueDate: '',
        startTime: '', dueTime: '', timeAllottedMinutes: '', requiresAttachment: false,
        isPrivate: false
    });
    // Only the assigner may change who can see a task, and only on one-off
    // work — the server ignores the field otherwise.
    const [canSetPrivate, setCanSetPrivate] = useState(false);

    const [selectedAssignees, setSelectedAssignees] = useState([]);
    // Vendors on the task (guest role), and who they were when it loaded.
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [existingVendors, setExistingVendors] = useState([]);
    // Assignees already on the task, so people who have since moved out of this
    // manager's team stay visible instead of being silently dropped.
    const [existingAssignees, setExistingAssignees] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [isRecurringTask, setIsRecurringTask] = useState(false);

    // --- convert to recurring ---
    const [convert, setConvert] = useState(false);
    // Back-dated work (server/utils/backdatedTasks.js): one past day, never a schedule.
    const [isBackdated, setIsBackdated] = useState(false);
    const [plannedDates, setPlannedDates] = useState([]);
    const [visibleRange, setVisibleRange] = useState(null);
    const [annotations, setAnnotations] = useState({});
    const [projection, setProjection] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const [taskRes, projRes, empRes] = await Promise.all([
                    api.get(`/tasks/${id}`),
                    api.get('/tasks/assignable-projects'),
                    api.get('/tasks/assignable-employees')
                ]);

                const task = taskRes.data;
                setFormData({
                    title: task.title || '',
                    description: task.description || '',
                    taskType: task.taskType || 'Project Task',
                    projectId: task.projectId?._id || '',
                    priority: task.priority || 'Medium',
                    startDate: task.startDate ? new Date(task.startDate).toISOString().split('T')[0] : '',
                    dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '',
                    startTime: task.startTime || '',
                    dueTime: task.dueTime || '',
                    timeAllottedMinutes: task.timeAllottedMinutes || '',
                    requiresAttachment: Boolean(task.requiresAttachment),
                    isPrivate: Boolean(task.isPrivate)
                });
                const me = JSON.parse(localStorage.getItem('user') || 'null');
                setCanSetPrivate(
                    task.assignedBy?._id === (me?.id || me?._id) &&
                    !task.isSelfAssigned && !task.recurringTaskId
                );
                setSelectedAssignees(task.assignees.map(a => a._id));
                setSelectedVendors((task.externalAssignees || []).map(v => v._id));
                setExistingVendors((task.externalAssignees || []).map(v => ({
                    _id: v._id, name: v.name, profilePic: v.profilePic, role: v.company || 'Guest'
                })));
                setExistingAssignees(task.assignees);
                setExistingAttachments(task.attachments || []);
                // A day generated by a schedule already belongs to one, so it has
                // nothing to convert into.
                setIsRecurringTask(Boolean(task.recurringTaskId));
                setIsBackdated(Boolean(task.isBackdated));

                setProjectsList(projRes.data || []);
                setEmployeesList(empRes.data || []);
            } catch (err) {
                console.error('Could not load task', err);
                Swal.fire('Error', err.response?.data?.message || 'Could not load this task.', 'error');
                goBack();
            } finally {
                setFetching(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // The same preview call AddTask makes: annotations for the months on screen,
    // and the projection once days are picked.
    useEffect(() => {
        if (!convert || !visibleRange) return;

        const timer = setTimeout(async () => {
            setPreviewLoading(plannedDates.length > 0 && selectedAssignees.length > 0);
            try {
                const res = await api.post('/tasks/recurring/preview', {
                    from: visibleRange.from,
                    to: visibleRange.to,
                    assigneeIds: selectedAssignees,
                    ...(plannedDates.length && selectedAssignees.length ? { dates: plannedDates } : {})
                });
                setAnnotations(res.data.annotations || {});
                setProjection(res.data.projection || null);
            } catch (err) {
                console.error('Could not preview the schedule', err);
                setProjection(null);
            } finally {
                setPreviewLoading(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [convert, visibleRange, plannedDates, selectedAssignees]);

    const handleVisibleRange = useCallback((from, to) => {
        setVisibleRange(prev => (prev?.from === from && prev?.to === to ? prev : { from, to }));
    }, []);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    // A task's start..due is a contiguous range, so it drives the same picker
    // the recurring day set uses.
    const oneOffDates = formData.dueDate
        ? datesBetween(formData.startDate || formData.dueDate, formData.dueDate)
        : (formData.startDate ? [formData.startDate] : []);

    const handleOneOffDates = (dates) => {
        setFormData(prev => ({
            ...prev,
            startDate: dates[0] || '',
            dueDate: dates.length ? dates[dates.length - 1] : ''
        }));
    };

    const dateFieldLabel = () => {
        if (convert) {
            if (plannedDates.length === 0) return 'Pick the days this runs on';
            return `${prettyDate(plannedDates[0])} → ${prettyDate(plannedDates[plannedDates.length - 1])}`;
        }
        if (!formData.dueDate) return 'Pick the start and due date';
        if (formData.startDate === formData.dueDate) return prettyDate(formData.dueDate);
        return `${prettyDate(formData.startDate)} → ${prettyDate(formData.dueDate)}`;
    };

    const projectionHeadline = () => {
        if (!convert || !projection) return null;
        const skipped = projection.perUser.reduce(
            (max, u) => Math.max(max, u.timeline.filter(t => !t.willRun && t.date).length), 0);
        const bits = [`${projection.targetCount} task${projection.targetCount === 1 ? '' : 's'} each`];
        if (skipped > 0) bits.push(`${skipped} day${skipped === 1 ? '' : 's'} skipped, rolled forward`);
        if (projection.summary?.endsOn) bits.push(`ends ${prettyDate(projection.summary.endsOn)}`);
        return bits.join(' · ');
    };

    // AttachMenu filters, caps and compresses before handing them over.
    const addNewFiles = (picked) => setNewFiles(prev => [...prev, ...picked]);

    /**
     * Converting replaces the task with a schedule, so it is confirmed rather
     * than saved silently — and the edits on screen are saved first, so the
     * schedule inherits what the user is looking at, not what was loaded.
     */
    const doConvert = async () => {
        const result = await Swal.fire({
            title: 'Make this a daily task?',
            html: `<p style="margin:0 0 6px">A schedule will be created for <strong>${plannedDates.length} day(s)</strong>, `
                + `generating a fresh copy each morning.</p>`
                + `<p style="margin:0;font-size:0.85rem;color:light-dark(#64748b, var(--theme-muted, #959ba5))">This one-off task will be archived — the schedule replaces it.</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Convert'
        });
        if (!result.isConfirmed) return;

        setLoading(true);
        try {
            const data = new FormData();
            Object.keys(formData).forEach(key => data.append(key, formData[key]));
            data.append('assigneeIds', JSON.stringify(selectedAssignees));
            data.append('keepAttachmentIds', JSON.stringify(existingAttachments.map(a => a._id)));
            newFiles.forEach(f => data.append('attachments', f));
            await api.put(`/tasks/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });

            const res = await api.post(`/tasks/recurring/from-task/${id}`, { plannedDates });

            await Swal.fire({
                icon: 'success',
                title: 'Now a daily task',
                text: res.data.generatedToday
                    ? "Today's copy has already gone out."
                    : 'The first copy goes out at 6am on the first scheduled day.',
                timer: 2800,
                showConfirmButton: false
            });
            navigate('/tasks?view=recurring');
        } catch (err) {
            Swal.fire('Could Not Convert', err.response?.data?.message || 'Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.taskType === 'Project Task' && !formData.projectId) {
            return Swal.fire('Pick a Project', 'Choose a project, or switch this to a Regular Office task.', 'warning');
        }
        if (selectedAssignees.length === 0 && (convert || selectedVendors.length === 0)) {
            return Swal.fire('No Assignees', 'A task must have at least one assignee.', 'warning');
        }

        if (convert) {
            // A schedule generates work for employees only.
            if (selectedVendors.length) {
                return Swal.fire('Guests on this task', 'Remove the guests before making this a daily task - schedules are for employees only.', 'warning');
            }
            if (plannedDates.length === 0) {
                return Swal.fire('Pick Some Days', 'Choose at least one day for this task to run.', 'warning');
            }
            return doConvert();
        }

        // The date field is a button now, so the browser can't enforce this.
        if (!formData.dueDate) {
            return Swal.fire('Pick a Due Date', 'Choose when this task is due.', 'warning');
        }
        if (formData.startTime && formData.dueTime && formData.dueTime <= formData.startTime) {
            return Swal.fire('Check Times', 'The due/end time must be after the start time.', 'warning');
        }

        setLoading(true);
        setUploadProgress(null);

        try {
            const data = new FormData();
            Object.keys(formData).forEach(key => data.append(key, formData[key]));
            data.append('assigneeIds', JSON.stringify(selectedAssignees));
            data.append('externalAssigneeIds', JSON.stringify(selectedVendors));
            data.append('keepAttachmentIds', JSON.stringify(existingAttachments.map(a => a._id)));
            newFiles.forEach(f => data.append('attachments', f));

            await api.put(`/tasks/${id}`, data, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (evt) => {
                    // No total stays indeterminate rather than showing NaN%.
                    if (!newFiles.length || !evt.total) return;
                    setUploadProgress(Math.min(100, Math.round((evt.loaded * 100) / evt.total)));
                }
            });

            await Swal.fire({ icon: 'success', title: 'Task Updated', timer: 1600, showConfirmButton: false });
            goBack();
        } catch (err) {
            console.error('Task update error:', err);
            Swal.fire('Update Failed', err.response?.data?.message || 'Could not update the task.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // People currently on the task stay in the picker even if they are no longer
    // inside this manager's team scope.
    const pickerOptions = [...employeesList];
    existingAssignees.forEach(ex => {
        if (ex && !pickerOptions.find(e => e._id === ex._id)) pickerOptions.push(ex);
    });

    if (fetching) {
        return (
            <div className="attendance-container fade-in" style={{ padding: '40px', textAlign: 'center', color: 'light-dark(#64748b, var(--theme-muted))' }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading task...
            </div>
        );
    }

    return (
        <div className="attendance-container fade-in">
            <div className="task-page-header">
                <button className="gts-btn secondary" onClick={() => goBack()}>
                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                </button>
                <h1 className="page-title header-no-margin">
                    <FontAwesomeIcon icon={faClipboardList} style={{ marginRight: '10px', color: 'light-dark(#215D7B, var(--theme-accent-ink))' }} />
                    {convert ? 'Make This a Daily Task' : 'Edit Task'}
                </h1>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="task-form-grid">

                    {/* ---------- LEFT: what the task is ---------- */}
                    <section className="task-form-card">
                        <div className="expense-section-title">
                            <FontAwesomeIcon icon={faInfoCircle} /> Task Details
                        </div>

                        <div className="form-group task-field">
                            <label className="input-label">Task Type</label>
                            <div className="type-toggle">
                                <button
                                    type="button"
                                    className={`type-toggle-btn ${formData.taskType === 'Project Task' ? 'active' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, taskType: 'Project Task' }))}
                                >
                                    <FontAwesomeIcon icon={faFolderOpen} /> Project Task
                                </button>
                                <button
                                    type="button"
                                    className={`type-toggle-btn ${formData.taskType === 'Regular Office Task' ? 'active' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, taskType: 'Regular Office Task', projectId: '' }))}
                                >
                                    <FontAwesomeIcon icon={faBuilding} /> Regular Office Task
                                </button>
                            </div>
                            <small className="task-hint">
                                {formData.taskType === 'Regular Office Task'
                                    ? 'Internal work not tied to any client project — no project needed.'
                                    : 'Work that belongs to a specific project.'}
                            </small>
                        </div>

                        <div className="form-group task-field">
                            <label className="input-label">Task Title *</label>
                            <input
                                type="text" name="title" className="custom-input"
                                value={formData.title} onChange={handleChange} required
                            />
                        </div>

                        <div className="form-group task-field field-grow">
                            <label className="input-label">Description</label>
                            <RichTextEditor
                                name="description" rows={8}
                                value={formData.description}
                                onChange={(v) => setFormData((f) => ({ ...f, description: v }))}
                            />
                        </div>

                        {formData.taskType === 'Project Task' && (
                            <div className="form-group task-field">
                                <label className="input-label">Project *</label>
                                <select
                                    name="projectId" className="swal2-select custom-select"
                                    value={formData.projectId} onChange={(e) => { handleChange(e); recordRecentPick('project', e.target.value); }} required
                                >
                                    <option value="">Select a project</option>
                                    <RecentOptions kind="project" items={projectsList} />
                                </select>
                            </div>
                        )}
                    </section>

                    {/* ---------- RIGHT: when, who, and what comes with it ---------- */}
                    <div className="task-form-col">
                        <section className="task-form-card">
                            <div className="expense-section-title">
                                <FontAwesomeIcon icon={faCalendarDay} /> Priority &amp; Schedule
                            </div>

                            {/* Schedules have no privacy, so a private task can't
                                become one (the server refuses it too). */}
                            {!isRecurringTask && !formData.isPrivate && !isBackdated && (
                                <div className={`rt-switch-row ${convert ? 'is-on' : ''}`}>
                                    <button
                                        type="button"
                                        className={`rt-switch ${convert ? 'is-on' : ''}`}
                                        onClick={() => setConvert(v => !v)}
                                        aria-pressed={convert}
                                        aria-label="Make this a daily task"
                                    />
                                    <div className="rt-switch-text">
                                        <strong>
                                            <FontAwesomeIcon icon={faRepeat} style={{ marginRight: '6px', color: 'light-dark(#215D7B, var(--theme-accent-ink))' }} />
                                            Make this a daily task
                                        </strong>
                                        <small>
                                            {convert
                                                ? 'A fresh copy goes out each morning on the days you pick. This one-off task is archived and the schedule replaces it.'
                                                : 'Turn this one-off into a run of daily tasks.'}
                                        </small>
                                    </div>
                                </div>
                            )}

                            <div className="task-field-row cols-2">
                                <div className="form-group task-field">
                                    <label className="input-label">Priority</label>
                                    <select
                                        name="priority" className="swal2-select custom-select"
                                        value={formData.priority} onChange={handleChange}
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                        <option value="Urgent">Urgent</option>
                                    </select>
                                </div>

                                <div className="form-group task-field">
                                    <label className="input-label">
                                        {convert ? 'Which days? *' : isBackdated ? 'Day the work was done *' : 'Start & due date *'}
                                    </label>
                                    <DatePickerField
                                        displayValue={dateFieldLabel()}
                                        isEmpty={convert ? plannedDates.length === 0 : !formData.dueDate}
                                        countBadge={convert && plannedDates.length > 0 ? `${plannedDates.length} days` : null}
                                        mode={convert ? 'multi' : isBackdated ? 'single' : 'range'}
                                        selected={convert ? plannedDates : oneOffDates}
                                        onChange={convert ? setPlannedDates : handleOneOffDates}
                                        annotations={convert ? annotations : {}}
                                        onVisibleRangeChange={convert ? handleVisibleRange : undefined}
                                        {...(convert ? { minDate: todayYmd() } : {})}
                                        {...(isBackdated ? { minDate: '2000-01-01', maxDate: todayYmd() } : {})}
                                    />
                                    {convert && projectionHeadline() && (
                                        <small className="task-hint">{projectionHeadline()}</small>
                                    )}
                                </div>
                            </div>

                            {!convert && !isRecurringTask && (
                                <div className="form-group task-field">
                                    <TaskTimeWindow
                                        startTime={formData.startTime}
                                        dueTime={formData.dueTime}
                                        timeAllottedMinutes={formData.timeAllottedMinutes}
                                        onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                                    />
                                </div>
                            )}

                            {!convert && !isRecurringTask && (
                                <div className="form-group task-field">
                                    <AttachmentRequirement
                                        value={formData.requiresAttachment}
                                        onChange={(v) => setFormData(prev => ({ ...prev, requiresAttachment: v }))}
                                    />
                                </div>
                            )}

                            {!convert && canSetPrivate && (
                                <div className="form-group task-field">
                                    <PrivateTaskToggle
                                        value={formData.isPrivate}
                                        onChange={(v) => setFormData(prev => ({ ...prev, isPrivate: v }))}
                                    />
                                </div>
                            )}

                            {convert && plannedDates.length > 0 && (
                                <ScheduleProjection
                                    projection={projection}
                                    loading={previewLoading}
                                    selectedCount={plannedDates.length}
                                />
                            )}
                        </section>

                        <section className="task-form-card">
                            <div className="expense-section-title">
                                <FontAwesomeIcon icon={faUsers} /> Assigned To
                                {selectedAssignees.length > 0 && (
                                    <span className="task-count-pill">{selectedAssignees.length} selected</span>
                                )}
                            </div>

                            <EmployeeMultiSelect
                                employees={pickerOptions}
                                selected={selectedAssignees}
                                onChange={setSelectedAssignees}
                                recentKey="task-assignee"
                                emptyText="No employees are mapped to you as their Team Lead yet — ask HR to set that up."
                            />

                            <small className="task-hint">
                                Removing someone deletes their progress on this task. People you add are notified immediately.
                            </small>
                        </section>

                        {!isRecurringTask && !convert && (
                            <VendorSection selected={selectedVendors} onChange={setSelectedVendors} keep={existingVendors} />
                        )}

                        <section className="task-form-card">
                            <div className="expense-section-title">
                                <FontAwesomeIcon icon={faPaperclip} /> Reference Images, Videos &amp; Documents
                            </div>

                            {existingAttachments.length > 0 && (
                                <>
                                    <label className="input-label">Current attachments</label>
                                    <div className="task-media-grid" style={{ marginBottom: '14px' }}>
                                        {existingAttachments.map(m => (
                                            <div key={m._id} className="task-media-item">
                                                {m.type === 'video' ? (
                                                    <video src={resolveMediaUrl(m.url)} controls preload="metadata" />
                                                ) : m.type === 'document' ? (
                                                    <a
                                                        className="task-media-doc"
                                                        href={resolveMediaUrl(m.url)} target="_blank" rel="noopener noreferrer"
                                                        title={m.fileName || 'Open document'}
                                                    >
                                                        <FontAwesomeIcon icon={faFileCode} />
                                                        <span>{m.fileName || 'Document'}</span>
                                                    </a>
                                                ) : (
                                                    <img src={resolveMediaUrl(m.url)} alt={m.fileName || 'attachment'} />
                                                )}
                                                <button
                                                    type="button" className="task-media-remove" title="Remove"
                                                    onClick={() => setExistingAttachments(prev => prev.filter(a => a._id !== m._id))}
                                                >✕</button>
                                                {m.status === 'processing_compression' && (
                                                    <span className="media-processing-chip">
                                                        <FontAwesomeIcon icon={faFilm} /> Optimising
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            <AttachMenu
                                recordingKey={`task:edit:${id}`}
                                recordingTitle="Task attachments"
                                label="Add attachment"
                                onFiles={addNewFiles}
                            />

                            <div className="task-upload-note">
                                <FontAwesomeIcon icon={faFilm} />
                                <span>
                                    Videos up to {MAX_VIDEO_MB}MB upload as-is and are watchable immediately.
                                    They&apos;re compressed and moved to permanent storage automatically at midnight.
                                </span>
                            </div>

                            {isCompressing && (
                                <p className="file-success-text" style={{ color: 'light-dark(#d97706, var(--theme-warning-ink))', marginTop: '8px', fontWeight: '600' }}>
                                    <FontAwesomeIcon icon={faSpinner} spin /> Preparing files...
                                </p>
                            )}

                            {newFiles.length > 0 && (
                                <div className="file-chips-list task-file-chips">
                                    {newFiles.map((f, i) => (
                                        <div key={i} className="file-chip">
                                            <span className="file-chip-name">
                                                {f.name} <em>({(f.size / 1048576).toFixed(1)}MB)</em>
                                            </span>
                                            <button
                                                type="button" className="file-chip-remove"
                                                onClick={() => setNewFiles(prev => prev.filter((_, idx) => idx !== i))}
                                            >✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    </div>
                </div>

                {/* Sticky so the form never has to be scrolled back to reach Save. */}
                <div className="task-form-footer">
                    {loading && (
                        <UploadProgress
                            progress={uploadProgress}
                            count={newFiles.length}
                            savingLabel={newFiles.length ? 'Saving to storage…' : 'Saving changes…'}
                        />
                    )}
                    <div className="task-form-footer-actions">
                        <span className="task-form-summary">
                            {selectedAssignees.length > 0
                                ? `${selectedAssignees.length} assignee${selectedAssignees.length > 1 ? 's' : ''}`
                                : 'No assignees yet'}
                            {existingAttachments.length + newFiles.length > 0 &&
                                ` · ${existingAttachments.length + newFiles.length} attachment${existingAttachments.length + newFiles.length > 1 ? 's' : ''}`}
                            {convert && plannedDates.length > 0 &&
                                ` · ${plannedDates.length} day${plannedDates.length > 1 ? 's' : ''} scheduled`}
                        </span>
                        <button type="submit" className="gts-btn primary" disabled={loading || isCompressing}>
                            {loading
                                ? <><FontAwesomeIcon icon={faSpinner} spin /> {convert ? 'Converting...' : 'Saving...'}</>
                                : convert
                                    ? <><FontAwesomeIcon icon={faRepeat} /> Convert to Daily Task</>
                                    : <><FontAwesomeIcon icon={faSave} /> Save Changes</>}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default EditTask;
